import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { InfoField } from "@/ui/components/DenseRow";
import { HIT_TARGET_MIN } from "@/ui/components/listRowA11y";
import { SectionCard } from "@/ui/components/SectionCard";
import { FinderDraftFields } from "@/ui/finder/FinderDraftFields";
import { useFinderMode } from "@/ui/finder/FinderModeContext";
import { FinderPrivacyProvision } from "@/ui/finder/FinderPrivacyProvision";
import { finderHost } from "@/ui/finder/finderHost";
import { FinderReuseMatch } from "@/ui/finder/FinderReuseMatch";
import {
  FINDER_NAME_SAVE_HINT,
  FINDER_PRIVACY_CONFIRMED,
  FINDER_PRIVACY_MISSING,
  FINDER_PROVISION_REQUIRED,
  FINDER_RELOAD_LOST,
  FINDER_STATUS_NONE,
  FINDER_STATUS_RECORDED,
  finderDraftHasPayload,
  finderMatchedBaseline,
  finderMissingContactLabels,
  finderPayloadWithoutName,
  finderProvisionLabel,
} from "@/ui/finder/finderMode";
import { WarningCircleIcon } from "phosphor-react-native";
import { useEffect, useRef, useState, type Ref, type RefObject } from "react";
import { Platform, Text, TextInput, View, type ScrollView } from "react-native";

const actionStyle = { alignSelf: "stretch" as const, minHeight: HIT_TARGET_MIN };

/**
 * Finder-Karte in der Aufnahme: Start, nach der Rückgabe die Zusammenfassung.
 * Die Datenschutzbestätigung bleibt im Gate. Die Art der Bereitstellung trägt die Pflegekraft hier ein.
 */
export function FinderIntakeCard({
  organizationId,
  intro,
  scrollRef,
  nameInputRef,
  onNotesSubmitEditing,
}: {
  organizationId: string | undefined;
  intro: string;
  scrollRef?: RefObject<ScrollView | null>;
  nameInputRef?: Ref<TextInput>;
  onNotesSubmitEditing?: () => void;
}) {
  const { theme } = useTheme();
  const finderMode = useFinderMode();
  const [editing, setEditing] = useState(false);
  const cardRef = useRef<View>(null);
  const titleRef = useRef<Text>(null);
  const seenFocus = useRef(0);
  const draft = finderMode.draft;
  const showSummary = finderMode.returned || finderDraftHasPayload(draft);
  const missingName = finderPayloadWithoutName(draft);
  const missingContacts = finderMissingContactLabels(draft);

  useEffect(() => {
    if (finderMode.focusToken === 0 || finderMode.focusToken === seenFocus.current) {
      return;
    }
    seenFocus.current = finderMode.focusToken;
    try {
      const card = cardRef.current;
      const title = titleRef.current;
      if (Platform.OS === "web") {
        if (card) {
          (card as unknown as { scrollIntoView?: (opts: ScrollIntoViewOptions) => void }).scrollIntoView?.(
            { block: "start" }
          );
        }
        if (!finderMode.provisionAlert) {
          (title as unknown as { focus?: () => void } | null)?.focus?.();
        }
        return;
      }
      if (card && scrollRef?.current) {
        const relative = finderHost.findNodeHandle(scrollRef.current);
        if (relative) {
          card.measureLayout(
            relative,
            (_x, y) => {
              scrollRef.current?.scrollTo({ y: Math.max(0, y - 8), animated: true });
            },
            () => {}
          );
        }
      }
      if (finderMode.provisionAlert) return;
      if (title) {
        const handle = finderHost.findNodeHandle(title);
        if (handle) finderHost.setAccessibilityFocus(handle);
      }
    } catch (error) {
      console.error(
        "Finder-Karte: Fokus fehlgeschlagen",
        error instanceof Error ? error.message : error,
      );
    }
  }, [finderMode.focusToken, finderMode.provisionAlert, scrollRef]);

  return (
    <View ref={cardRef} collapsable={false}>
      <SectionCard
        title="Finder"
        titleRef={titleRef}
        titleAsHeader={showSummary}
        bodyStyle={{ paddingVertical: theme.space.sm }}
      >
        {showSummary ? (
          <View style={{ gap: theme.space.sm }}>
            {finderMode.recoveredWithoutDraft ? (
              <Text
                accessibilityRole="alert"
                style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
              >
                {FINDER_RELOAD_LOST}
              </Text>
            ) : null}
            {finderMode.handoverNote ? (
              <Text
                accessibilityRole="alert"
                style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
              >
                {finderMode.handoverNote}
              </Text>
            ) : null}
            <Text
              style={{
                color: theme.color.text,
                fontSize: theme.fontSize.body,
                fontWeight: "700",
              }}
            >
              {finderDraftHasPayload(draft) ? FINDER_STATUS_RECORDED : FINDER_STATUS_NONE}
            </Text>
            {finderDraftHasPayload(draft) ? (
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: theme.space.md,
                }}
              >
                <InfoField label="Name" value={draft.displayName.trim()} />
                <InfoField label="Telefon" value={draft.phone.trim()} />
                <InfoField label="E-Mail" value={draft.email.trim()} />
                <InfoField label="Anschrift" value={draft.address.trim()} />
                <InfoField label="Hinweise" value={draft.notes.trim()} />
              </View>
            ) : null}
            {finderDraftHasPayload(draft) && missingContacts.length > 0 ? (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: theme.space.xs,
                }}
              >
                <WarningCircleIcon size={18} color={theme.color.warning} />
                <Text
                  style={{
                    color: theme.color.warningText,
                    fontSize: theme.fontSize.body,
                    flex: 1,
                  }}
                >
                  Nicht angegeben: {missingContacts.join(", ")}
                </Text>
              </View>
            ) : null}
            {finderDraftHasPayload(draft) ? (
              <Text
                accessibilityRole={draft.privacyDocumented ? undefined : "alert"}
                style={{
                  color: draft.privacyDocumented ? theme.color.text : theme.color.dangerText,
                  fontSize: theme.fontSize.body,
                }}
              >
                {draft.privacyDocumented ? FINDER_PRIVACY_CONFIRMED : FINDER_PRIVACY_MISSING}
              </Text>
            ) : null}
            {finderMode.matchPrivacyNotice ? (
              <Text
                accessibilityRole="alert"
                style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
              >
                {finderMode.matchPrivacyNotice}
              </Text>
            ) : null}
            {finderMode.matchDetachNotice ? (
              <Text
                accessibilityRole="alert"
                style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
              >
                {finderMode.matchDetachNotice}
              </Text>
            ) : null}
            {finderMode.provisionAlert &&
            draft.privacyDocumented &&
            draft.privacyProvisionKind == null ? (
              <Text
                accessibilityRole="alert"
                style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
              >
                {FINDER_PROVISION_REQUIRED}
              </Text>
            ) : null}
            {finderDraftHasPayload(draft) ? (
              <Text style={{ color: theme.color.text, fontSize: theme.fontSize.body }}>
                Art der Bereitstellung: {finderProvisionLabel(draft.privacyProvisionKind)}
              </Text>
            ) : null}
            <FinderPrivacyProvision
              optional={!draft.privacyDocumented}
              focusRequest={
                finderMode.provisionAlert && !draft.privacyProvisionKind
                  ? finderMode.provisionFocus
                  : 0
              }
              value={draft.privacyProvisionKind}
              onChange={(privacyProvisionKind) =>
                finderMode.updateDraft({ privacyProvisionKind })
              }
            />
            {missingName && !editing ? (
              <Text
                accessibilityRole="alert"
                style={{ color: theme.color.dangerText, fontSize: theme.fontSize.body }}
              >
                {FINDER_NAME_SAVE_HINT}
              </Text>
            ) : null}
            {editing ? (
              <FinderDraftFields
                draft={draft}
                onChange={finderMode.updateDraft}
                nameHint={missingName ? FINDER_NAME_SAVE_HINT : null}
                nameInputRef={nameInputRef}
                onNotesSubmitEditing={onNotesSubmitEditing}
              />
            ) : null}
            <FinderReuseMatch
              organizationId={organizationId}
              draft={draft}
              onApply={(hit) => {
                const displayName = hit.display_name;
                const phone = hit.phone ?? draft.phone;
                const email = hit.email ?? draft.email;
                finderMode.updateDraft({
                  matchedContactId: hit.id,
                  matchedBaseline: finderMatchedBaseline(
                    { displayName, phone, email },
                    draft.privacyDocumented,
                  ),
                  displayName,
                  phone,
                  email,
                });
              }}
            />
            {draft.matchedContactId ? (
              <AppButton
                variant="outline"
                style={actionStyle}
                onPress={() => finderMode.releaseMatchedContact()}
              >
                Treffer lösen
              </AppButton>
            ) : null}
            <AppButton
              variant="outline"
              style={actionStyle}
              onPress={() => setEditing((open) => !open)}
            >
              {editing ? "Angaben einklappen" : "Angaben bearbeiten"}
            </AppButton>
            {finderMode.lockError ? (
              <Text
                accessibilityRole="alert"
                style={{ color: theme.color.dangerText, fontSize: theme.fontSize.body }}
              >
                {finderMode.lockError}
              </Text>
            ) : null}
            <AppButton
              variant="ghost"
              style={actionStyle}
              onPress={() => {
                void finderMode.start();
              }}
            >
              Finder-Modus erneut starten
            </AppButton>
          </View>
        ) : (
          <View style={{ gap: theme.space.sm }}>
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.small,
              }}
            >
              {intro}
            </Text>
            {finderMode.lockError ? (
              <Text
                accessibilityRole="alert"
                style={{ color: theme.color.dangerText, fontSize: theme.fontSize.body }}
              >
                {finderMode.lockError}
              </Text>
            ) : null}
            <AppButton
              variant="outline"
              style={actionStyle}
              onPress={() => {
                void finderMode.start();
              }}
            >
              Finder-Modus starten
            </AppButton>
          </View>
        )}
      </SectionCard>
    </View>
  );
}
